import { motion, AnimatePresence } from 'framer-motion'
import { Award, ArrowRight, Home } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface ModuleMilestoneProps {
    isOpen: boolean
    onClose: () => void
    moduleTitle: string
    className: string
    /** colour accent — defaults to gold for church/bible, green-teal for homeschool */
    accentColor?: string
    /** Where "go to dashboard" navigates */
    dashboardPath?: string
    /** Filled star/cross for church, graduation cap for homeschool */
    variant?: 'church' | 'homeschool' | 'elite'
}

const GOLD = '#c8a84b'
const GREEN = '#4ade80'
const BLUE = '#1430a3ff'
const DARK = '#0a1220'

export function ModuleMilestone({
    isOpen, onClose, moduleTitle, className,
    accentColor, dashboardPath = '/', variant = 'homeschool',
}: ModuleMilestoneProps) {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const accent = accentColor ?? (
        variant === 'church' ? GOLD :
            variant === 'elite' ? BLUE : GREEN
    )

    const emoji = variant === 'church' ? '✝️' : variant === 'elite' ? '👑' : '🎓'

    return (
        <AnimatePresence>
            {isOpen && (
                <div style={{
                    position: 'fixed', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 3000, padding: 20,
                }}>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={onClose}
                        style={{ position: 'absolute', inset: 0, background: 'rgba(6,6,12,0.92)', backdropFilter: 'blur(14px)' }}
                    />

                    {/* Certificate card */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.88, y: 24 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.88, y: 24 }}
                        transition={{ type: 'spring', damping: 20, stiffness: 120 }}
                        style={{
                            position: 'relative', width: '100%', maxWidth: 520,
                            background: DARK, border: `1px solid ${accent}44`,
                            borderRadius: 28, padding: '56px 40px', textAlign: 'center',
                            boxShadow: `0 32px 80px rgba(0,0,0,0.85), inset 0 0 80px ${accent}08`,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Corner accents */}
                        {['tl', 'tr', 'bl', 'br'].map(pos => (
                            <div key={pos} style={{
                                position: 'absolute',
                                top: pos.startsWith('t') ? 0 : undefined,
                                bottom: pos.startsWith('b') ? 0 : undefined,
                                left: pos.endsWith('l') ? 0 : undefined,
                                right: pos.endsWith('r') ? 0 : undefined,
                                width: 52, height: 52,
                                borderTop: pos.startsWith('t') ? `2px solid ${accent}` : undefined,
                                borderBottom: pos.startsWith('b') ? `2px solid ${accent}` : undefined,
                                borderLeft: pos.endsWith('l') ? `2px solid ${accent}` : undefined,
                                borderRight: pos.endsWith('r') ? `2px solid ${accent}` : undefined,
                                opacity: 0.35,
                            }} />
                        ))}

                        {/* Glow */}
                        <div style={{
                            position: 'absolute', top: '-15%', left: '50%', transform: 'translateX(-50%)',
                            width: '90%', height: 120,
                            background: `radial-gradient(ellipse at center, ${accent}20 0%, transparent 70%)`,
                            pointerEvents: 'none',
                        }} />

                        {/* Icon */}
                        <motion.div
                            initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }}
                            transition={{ delay: 0.25, type: 'spring', stiffness: 200 }}
                            style={{
                                width: 80, height: 80, borderRadius: '50%',
                                background: `${accent}18`, border: `2px solid ${accent}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 28px', color: accent,
                            }}
                        >
                            <Award size={36} />
                        </motion.div>

                        {/* Subtitle label */}
                        <motion.p
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.35 }}
                            style={{ fontSize: 11, fontWeight: 800, color: accent, letterSpacing: '3.5px', textTransform: 'uppercase', marginBottom: 14 }}
                        >
                            {t('milestone.reached', 'Milestone Reached')} {emoji}
                        </motion.p>

                        {/* Module title */}
                        <motion.h1
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.42 }}
                            style={{ fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: '-0.8px', marginBottom: 10, lineHeight: 1.2 }}
                        >
                            {moduleTitle}
                        </motion.h1>

                        {/* Class name */}
                        <motion.p
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            transition={{ delay: 0.52 }}
                            style={{ fontSize: 15, color: 'rgba(255,255,255,0.45)', marginBottom: 36, lineHeight: 1.55, maxWidth: 360, margin: '0 auto 36px' }}
                        >
                            {t('milestone.desc', { className, defaultValue: `You've successfully completed this module in {{className}}.` })}
                        </motion.p>

                        {/* Buttons */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.65 }}
                            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                        >
                            <button
                                onClick={onClose}
                                style={{
                                    background: `linear-gradient(135deg, ${accent}, ${accent}bb)`,
                                    color: DARK, border: 'none', borderRadius: 14,
                                    padding: '15px 28px', fontSize: 15, fontWeight: 800, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                    boxShadow: `0 8px 24px ${accent}44`,
                                    transition: 'transform 0.15s, box-shadow 0.15s',
                                }}
                                onMouseOver={e => (e.currentTarget.style.transform = 'translateY(-2px)')}
                                onMouseOut={e => (e.currentTarget.style.transform = 'translateY(0)')}
                            >
                                {t('milestone.continue', 'Continue Learning')} <ArrowRight size={17} />
                            </button>

                            <button
                                onClick={() => { onClose(); navigate(dashboardPath) }}
                                style={{
                                    background: 'transparent', color: 'rgba(255,255,255,0.35)',
                                    border: 'none', fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer', padding: 10, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', gap: 6,
                                }}
                            >
                                <Home size={13} /> {t('milestone.dashboard', 'Back to Dashboard')}
                            </button>
                        </motion.div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    )
}
